import {
  InputChips,
  InputQuantity,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { RecursivePartial } from '@farmshare/utils';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { filter, map } from 'lodash';
import { useEffect } from 'react';
import { Form } from 'react-bootstrap';

import {
  EnumCutsheetTrimTrimSelection,
  ProcessorCapability,
} from 'lib/graphql';

import {
  formatInputChipLabel,
  useCutsheetProcessorCapabilities,
} from './helpers';
import { CutsheetFormData, Trim } from './types';

interface CutsheetTrimSectionProps {
  fP: FormikProps<CutsheetFormData>;
  capabilities: RecursivePartial<ProcessorCapability>[];
  readOnly?: boolean;
}

export function CutsheetTrimSection({
  fP,
  capabilities,
  readOnly = false,
}: CutsheetTrimSectionProps) {
  const processorCapabilities = useCutsheetProcessorCapabilities(
    fP,
    capabilities,
  );

  // Set the initial values for extras and trim based on the selected inspection level
  useEffect(() => {
    const selectedInspectionLevel = fP.values.inspectionLevel;

    if (!selectedInspectionLevel) {
      return;
    }

    const mappedTrim = map(
      filter(processorCapabilities?.trim, (item) => item?.isActive),
      (item: Trim) => ({
        ...item,
        isActive: false,
        quantity: item?.minLbs ?? 0,
        rank: 1,
        disabled: false,
        trimSelection: (processorCapabilities?.trimSelection ??
          'allowSelectionByWeight') as EnumCutsheetTrimTrimSelection,
      }),
    ) as unknown as Trim[];

    if (fP.initialValues.trim?.length) {
      for (const item of fP.initialValues.trim) {
        const index = mappedTrim.findIndex(
          (mappedItem) =>
            mappedItem.name.trim().toLowerCase() ===
            item.name.trim().toLowerCase(),
        );
        if (index !== -1) {
          mappedTrim[index].isActive = true;
          mappedTrim[index].quantity = item.quantity;
          mappedTrim[index].rank = item.rank;
          mappedTrim[index].disabled = item.disabled;
          mappedTrim[index].trimSelection =
            (processorCapabilities?.trimSelection ??
              'allowSelectionByWeight') as EnumCutsheetTrimTrimSelection;
        }
      }
    }

    fP.setFieldValue('trim', mappedTrim);
    fP.setFieldValue(
      'canSelectMoreThanOneTrim',
      processorCapabilities?.canSelectMoreThanOneTrim ?? true,
    );
    fP.setFieldValue(
      'trimSelection',
      processorCapabilities?.trimSelection ?? 'allowSelectionByWeight',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fP.values.inspectionLevel, processorCapabilities?.trim]);

  const { trim } = fP.values;

  if (!trim || trim.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 2, height: 1 }} elevation={1}>
      <CardHeader title="Trim" />
      <Stack direction="column" component={CardContent} spacing={2}>
        {map(trim, (item: Trim, index: number) => (
          <Stack
            spacing={1}
            justifyContent="space-between"
            key={`${item.name}-${index}`}
            direction={{ xs: 'column', sm: 'row' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: 300 }}>
              <InputChips
                gap={2}
                disabled={readOnly}
                options={[
                  {
                    label: formatInputChipLabel(item),
                    value: item.name,
                    isActive: trim[index]?.isActive,
                    disabled: fP.values.trim?.[index]?.disabled,
                  },
                ]}
                size="sm"
                action={() => {
                  if (fP.values.trim?.[index]?.disabled) {
                    return;
                  }

                  const itemIndex = fP.values.trim?.findIndex(
                    (trim) => trim?.name === item.name,
                  );
                  if (itemIndex === undefined || itemIndex === -1) {
                    fP.setFieldValue(`trim.${index}`, {
                      ...item,
                      isActive: true,
                      rank:
                        (fP.values.trim?.filter((t) => t?.isActive).length ??
                          0) + 1,
                    });
                    fP.setFieldValue('trimSelection', {
                      ...fP.values,
                      trimSelection:
                        fP.values.trimSelection ?? 'allowSelectionByWeight',
                    });

                    return;
                  }

                  // toggle the trim
                  fP.setFieldValue(`trim.${itemIndex}`, {
                    ...item,
                    rank:
                      (fP.values.trim?.filter((t) => t?.isActive).length ?? 0) +
                      1,
                    isActive: !fP.values.trim?.[itemIndex]?.isActive,
                  });
                  fP.setFieldTouched(`trim.${itemIndex}.quantity`, true);
                  fP.setFieldTouched(`trim.${itemIndex}.rank`, true);
                }}
              />
            </Box>
            {item.isActive && (
              <Stack
                spacing={2}
                sx={{ flex: 1, alignItems: 'flex-start' }}
                direction={{ xs: 'column', sm: 'row' }}
              >
                <InputText
                  label="Rank"
                  nameOverride={`trim.${index}.rank`}
                  type="number"
                  size="sm"
                  floatingLabel
                  required={fP.values.trim?.[index]?.isActive}
                  min={1}
                  disabled={fP.values.trim?.[index]?.disabled || readOnly}
                />
                {fP.values.trimSelection === 'allowSelectionByWeight' &&
                  Boolean(item.minLbs) && (
                    <InputQuantity
                      nameOverride={`trim.${index}.quantity`}
                      step={1}
                      min={item.minLbs}
                      disabled={fP.values.trim?.[index]?.disabled || readOnly}
                    />
                  )}
                {fP.values.trimSelection === 'allowSelectionByPercentage' && (
                  <InputText
                    label="%"
                    nameOverride={`trim.${index}.quantity`}
                    type="number"
                    step={1}
                    min={0}
                    max={100}
                    floatingLabel
                    disabled={fP.values.trim?.[index]?.disabled || readOnly}
                    size="sm"
                  />
                )}
              </Stack>
            )}

            {/* Display an error message if only one trim can be selected */}
            {fP.errors.canSelectMoreThanOneTrim &&
              fP.values.trim?.[index]?.isActive && (
                <Form.Control.Feedback
                  type="invalid"
                  style={{ display: 'block' }}
                >
                  <div>{fP.errors.canSelectMoreThanOneTrim}</div>
                </Form.Control.Feedback>
              )}
          </Stack>
        ))}

        {fP.values.trimSelection === 'allowSelectionByPercentage' && (
          <>
            <hr className="mt-3" />
            <div className="text-center">
              <span className={fP.errors.trimSelection ? 'text-danger' : ''}>
                Total :{' '}
                {fP.values.trim?.reduce(
                  (total, trim) =>
                    total + (trim?.isActive ? trim?.quantity || 0 : 0),
                  0,
                )}
                %
              </span>
            </div>

            {fP.errors.trimSelection && (
              <div className="text-center text-danger">
                {fP.errors.trimSelection}
              </div>
            )}
          </>
        )}
      </Stack>
      <CardContent>
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5, fontSize: '0.75rem' }}
          >
            Notes for Trim
          </Typography>
          <InputTextArea
            nameOverride="notes.trim"
            hint="Add any notes about your trim."
            disabled={readOnly}
            label="Notes"
            rows={2}
            showLabel={false}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
